// VideoTwoFactorGate.tsx — the Videos pages' two-factor write lock (1.27.70, owner 2026-09-30).
//
// Members without two-factor sign-in can watch everything on the Videos pages (the Videos home,
// a video's watch page, Flash), but every interaction is locked: comments and replies,
// reactions, saving, sharing, clipping, embedding, downloading, following, recommendation
// changes. Reporting and appeals stay open (hooks/writeGate.ts). A yellow flyout says so when
// the page opens and again whenever a locked action is tried; "Not now" hides it for the
// session. Owner scope ruling: the Videos pages only. The same video elsewhere (the home feed,
// its post page) is not locked, and the lock is on-screen only.
import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '@/icons';
import { media } from '@/theme/media';
import { useSession } from '@/hooks';
import { WriteGateContext, type WriteGate } from '@/hooks/writeGate';

const dismissalKey = (userId: string) => `truxsocial:video-2fa-dismissed:${userId}`;

function wasDismissed(userId: string): boolean {
  try {
    return sessionStorage.getItem(dismissalKey(userId)) === 'yes';
  } catch {
    return false;
  }
}

const Flyout = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 90; /* above the player, below dialogs (100) and toasts (200) */
  width: min(420px, calc(100vw - 32px));
  display: flex;
  gap: 12px;
  padding: 16px 18px;
  border-radius: ${({ theme }) => theme.radius.lg};
  /* Soft yellow fill, opaque (glass-off rule), no outline. */
  background: ${({ theme }) => theme.color.cautionSoft};
  border: 1px solid transparent;
  box-shadow: ${({ theme }) => theme.elevation.md};
  color: ${({ theme }) => theme.color.textPrimary};
  ${media.mobile`
    left: 16px;
    right: 16px;
    bottom: calc(80px + env(safe-area-inset-bottom));
    width: auto;
  `}
  > svg {
    flex: none;
    margin-top: 2px;
    color: ${({ theme }) => theme.color.caution};
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  h2 {
    margin: 0 0 6px;
    font: 600 ${({ theme }) => theme.fontSize.md} ${({ theme }) => theme.font.body};
    line-height: 1.3;
  }
  p {
    margin: 0 0 12px;
    font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
    line-height: 1.5;
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Enable = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.caution};
  color: ${({ theme }) => theme.color.onCaution};
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  text-decoration: none;
`;

const NotNow = styled.button`
  padding: 7px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  cursor: pointer;
`;

export function TwoFactorFlyout({ onClose }: { onClose: () => void }) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <Flyout role="dialog" aria-modal="false" aria-labelledby={titleId}>
      <Icon name="shield" size={20} />
      <Body>
        <h2 id={titleId}>Turn on two-factor sign-in to join in on videos</h2>
        <p>
          You can watch everything. Commenting, reacting, saving and sharing videos need two-factor
          sign-in, which keeps video free of spam and fake accounts.
        </p>
        <Buttons>
          <Enable to="/settings/privacy">Turn on two-factor</Enable>
          <NotNow type="button" onClick={onClose}>
            Not now
          </NotNow>
        </Buttons>
      </Body>
    </Flyout>
  );
}

/** Wrap a Videos page. Signed-out visitors and members with two-factor are unaffected. */
export function VideoTwoFactorGate({ children }: { children: ReactNode }) {
  const { currentUser } = useSession();
  const userId = currentUser?.id ?? '';
  // The owner's own session payload always carries twoFactorEnabled; only an explicit false locks.
  const locked = Boolean(currentUser) && currentUser?.twoFactorEnabled === false;
  // Derived, not synced in an effect: shown on arrival until "Not now" (remembered for the
  // session, per account), and again whenever a locked action is tried. Both flags carry the
  // account they belong to, so switching accounts starts fresh.
  const [closedFor, setClosedFor] = useState<string | null>(null);
  const [remindedFor, setRemindedFor] = useState<string | null>(null);
  const dismissed = closedFor === userId || wasDismissed(userId);
  const open = locked && (remindedFor === userId || !dismissed);

  const remind = useCallback(() => setRemindedFor(userId), [userId]);
  const close = useCallback(() => {
    setRemindedFor(null);
    setClosedFor(userId);
    try {
      sessionStorage.setItem(dismissalKey(userId), 'yes');
    } catch {
      /* in-memory dismissal still works when storage is unavailable */
    }
  }, [userId]);

  const gate = useMemo<WriteGate>(() => ({ locked, remind }), [locked, remind]);

  return (
    <WriteGateContext.Provider value={gate}>
      {children}
      {/* Portalled to <body>: the shell's route transition transforms the page column, and a
          transformed ancestor would pin a fixed flyout to the bottom of the (long) page instead
          of the screen, below the fold on arrival. */}
      {locked && open && createPortal(<TwoFactorFlyout onClose={close} />, document.body)}
    </WriteGateContext.Provider>
  );
}
